import * as PIXI from 'pixi.js-legacy';
import _ from 'lodash';
import deepEqual from 'fast-deep-equal';

import ProppingItem from './ProppingItem';

import emitter from '../emitter';

import {
  getMatchingConnections,
  getBounds,
  getProppingBounds,
} from '../services/products/models';
import productService, { getProduct } from '../services/products';
import proppingService from '../services/propping';

import { ITEM_MOVING, SCENE_REDRAWN } from '../settings/events';

import { replace } from '../state/tac/replace';
import tacHelpers from '../state/tac/tacHelpers';

import idGenerator from '../util/aactools/idGenerator';
import adjustableContainer from './util/adjustableContainer';
import getItemConfig from './util/getItemConfig';
import { flatten } from '../util/array';
import geometry from './util/geometry';
import project from './util/project';

export default class MultiParentClothesRail extends ProppingItem {
  constructor(args) {
    super(args);
    this.getAlpha = this.getAlpha.bind(this);
    this.onSceneRedrawn = this.onSceneRedrawn.bind(this);
    this.onItemMove = this.onItemMove.bind(this);

    this.db_onSceneRedrawn = _.debounce(this.onSceneRedrawn, 50, {
      leading: true,
      trailing: false,
    });
    emitter.on(ITEM_MOVING, this.onItemMove);
    emitter.on(SCENE_REDRAWN, this.db_onSceneRedrawn);
  }

  destroy(args) {
    emitter.off(ITEM_MOVING, this.onItemMove);
    emitter.off(SCENE_REDRAWN, this.db_onSceneRedrawn);
    super.destroy(args);
  }

  update(args) {
    this.config = getItemConfig(args.item);
    this.outerPipe = this.outerPipe || getProduct(this.config.outerId);
    this.selfMoving = this.app.dragging?.item?.itemid === args.item?.itemid;

    const { localItem, selfMoving } = this;

    if (args.item && args.item !== localItem) {
      const updatedItem = Object.assign({}, args.item);
      if (localItem && productService.isExtendable(updatedItem)) {
        if (!selfMoving) {
          updatedItem.width = localItem.width;
          updatedItem.height = localItem.height;
        }
        updatedItem.onlyConnectTo = localItem.onlyConnectTo;
        updatedItem.connectsTo = localItem.connectsTo;
      }

      this.localItem = updatedItem;
    }

    super.update(args);
  }

  setSpriteVisibilities(isExtendable) {
    if (this.sprite) {
      this.sprite.visible = !isExtendable;
    }
    if (this.adjustableContainer) {
      this.adjustableContainer.visible = isExtendable;
    }
  }

  drawAdjustable(propping) {
    const { config, ratio, localItem } = this;
    const { shouldShowPropping, proppingName, singleProppingBounds } = propping;

    const outerSize = project.outer(localItem);

    this.adjustableContainer = adjustableContainer(
      this.adjustableContainer,
      outerSize,
      {
        leftId: config.innerId,
        rightId: config.outerId,
      },
      ratio,
      shouldShowPropping ? { proppingName, singleProppingBounds } : {}
    );

    this.adjustableContainer.alpha = this.getAlpha(localItem.width);

    if (!this.adjustableContainer.parent) {
      this.addChild(this.adjustableContainer);
    }
  }

  drawFixed(propping) {
    const { item, ratio, localItem } = this;
    const { shouldShowPropping, proppingName, singleProppingBounds } = propping;

    const outerSize = project.outer(localItem);

    const texture = shouldShowPropping
      ? PIXI.Loader.shared.resources[proppingName].texture
      : PIXI.Loader.shared.resources[item.id].texture;

    this.sprite = this.sprite || new PIXI.Sprite();
    this.sprite.texture = texture;
    this.sprite.width = outerSize.width * ratio;
    this.sprite.height =
      (shouldShowPropping
        ? singleProppingBounds.size.height
        : outerSize.height) * ratio;

    if (!this.sprite.parent) {
      this.addChild(this.sprite);
    }
  }

  draw(movingItem) {
    const { item, options } = this;
    this.visible = !item.hidden;

    const isExtendable = productService.isExtendable(item);

    const { proppingName, singleProppingBounds } = this.getResourceName(
      movingItem,
      isExtendable
    );

    if (singleProppingBounds) {
      item.propping = singleProppingBounds.id;
    }

    const shouldShowPropping =
      this.shouldShowPropping([singleProppingBounds]) &&
      !options.measurementsActive;

    const proppingInfo = {
      shouldShowPropping,
      proppingName,
      singleProppingBounds,
    };

    if (isExtendable) {
      this.drawAdjustable(proppingInfo);
    } else {
      this.drawFixed(proppingInfo);
    }

    this.setSpriteVisibilities(isExtendable);
  }

  getResourceName(movingItem, isExtendable) {
    const { config, item, parentItem, parent, outerPipe } = this;
    const proppingItem = isExtendable ? outerPipe : item;

    const topAncestor = tacHelpers.getTopAncestor(this.app.tac, parentItem);

    const topAncestorSprite =
      isExtendable &&
      topAncestor &&
      topAncestor.itemid !== parentItem.itemid &&
      this.app.itemContainer &&
      this.app.itemContainer.getSprite(topAncestor.itemid);

    const useSmallestPropping = this.localItem.width > config.maxWidth;
    if (this.recalcPropping) {
      this.item.propping = null;
    }

    const singleProppingBounds = useSmallestPropping
      ? proppingService.smallestPropping(proppingItem)
      : proppingService.pickPropping({
          isDynamic: true,
          tac: this.app.tac,
          item,
          proppingItem,
          parentItem,
          parentContainer: parent,
          grandParentContainer:
            topAncestorSprite && topAncestorSprite.rightContainer,
          movingItem,
        });

    this.recalcPropping = useSmallestPropping;

    let proppingName;

    if (singleProppingBounds) {
      if (!isExtendable) {
        proppingName = `${item.id}_propping_${singleProppingBounds.id}`;
      } else {
        proppingName = `${config.outerId}_propping_${singleProppingBounds.id}`;
      }
      return {
        proppingName,
        singleProppingBounds,
      };
    }
  }

  getBounds() {
    const { item } = this;

    const bounds = super.getBounds();
    const proppingItem = productService.isExtendable(item)
      ? this.outerPipe
      : this.item;
    const proppingHeight = getProppingBounds(proppingItem).find(
      bounds => bounds.id === '1'
    ).size.height;
    bounds.height = proppingHeight * this.ratio;
    return bounds;
  }

  getHitArea() {
    const { scaledVertices } = this.data;
    const { item, ratio, outerPipe, config } = this;
    const padding = 8;

    let proppingHeight;
    if (item) {
      const proppingItem = productService.isExtendable(item) ? outerPipe : item;
      proppingHeight = getProppingBounds(proppingItem)[0].size.height;
    } else {
      proppingHeight = getProppingBounds(getProduct(config.fullId))[0].size
        .height;
    }

    const vertices = [
      [scaledVertices[0][0], scaledVertices[0][1] + proppingHeight * ratio],
      [scaledVertices[7][0], scaledVertices[7][1] - padding],
      [scaledVertices[6][0] - padding, scaledVertices[6][1] - padding],
      //[scaledVertices[6][0] - padding, scaledVertices[6][1]],
      [
        scaledVertices[1][0] - padding,
        scaledVertices[1][1] + proppingHeight * ratio,
      ],
    ];

    const polygon = {
      regions: [vertices],
    };

    return {
      polygon,
    };
  }

  getAllChildren() {
    return [];
  }

  eraseDropAreas() {}

  displayDropArea() {}

  getAlpha(width) {
    const { config, selfMoving, localItem, item } = this;

    if (selfMoving) {
      return 1; // dragging around, undocked
    }
    if (width < config.minWidth) {
      return 0; // invisible
    }
    if (localItem.partnerBlocked) {
      return 0; // something between here and connectsTo
    }
    if (!localItem.connectsTo && idGenerator.hasRealId(item)) {
      return 0; // invisible and considered not here (temporary parent switch)
    }
    if (width > config.maxWidth) {
      return 0.4; // dimmed
    }
    return 1; // opaque
  }

  railCanReach(stateTac, targetSlot, left, moveItem) {
    const { config } = this;
    const tac = _.cloneDeep(stateTac);
    replace(tac.items, moveItem);

    const right = targetSlot.parent || targetSlot;
    const tacRight = tacHelpers.getItem(tac, right.itemid);
    const gtacRight = tacHelpers.getGlobalCoords(tacRight, tac);
    const gPos = tacHelpers.getGlobalCoords(this.item, tac);

    const bounds = getBounds(this.item, 'space');
    const crRect = {
      x: gPos.x,
      y: gPos.y - bounds.size.height,
      z: gPos.z,
      height: bounds.size.height,
      width: gtacRight.x - gPos.x + config.mountOffset + 1, // a little wider so it collides
      depth: bounds.size.depth,
    };
    if (crRect.width < 0) {
      return false;
    }

    const rects = tacHelpers.getRawRects(tac, left);
    const blockers = rects.filter(blocker =>
      geometry.collides(blocker, crRect)
    );
    // get rid of self and parents
    const reached = blockers.filter(
      item => !tacHelpers.isAncestor(tac, item, this.item)
    );
    // make sure we collide with intended frame and any other collisions is in same stack
    const topRight = tacHelpers.getTopAncestor(tac, tacRight);
    if (
      reached.every(
        item =>
          topRight.itemid === item.itemid ||
          tacHelpers.isAncestor(tac, topRight, item)
      )
    ) {
      return true;
    }
    return false;
  }

  /*
  returns false if update failed, true if successful, and undefined if not applicable
  */
  onItemMove(tac, moveItem) {
    // start of left stack
    const left = tacHelpers.getTopAncestor(tac, this.item);
    const isExtendable = productService.isExtendable(this.item);
    if (!moveItem) {
      // nothing moved but we need to set ourselves up. imagine parent moving 0 pixels
      moveItem = left;
    }
    // there are many reasons not to care about this:

    if (!Number.isFinite(this.item.itemid)) {
      // new items from swiper aren't interesting
      this.selfMoving = true;

      // ...but we need to make sure that they are at set to the minimum width,
      // in order to be drawn correctly
      if (isExtendable && this.localItem.width < this.config.minWidth) {
        this.localItem.width = this.config.minWidth;
        this.draw();
      }

      return;
    }

    if (!Number.isFinite(moveItem.itemid)) {
      // new items from swiper aren't interesting
      return;
    }

    if (!isExtendable) {
      // we're mounted inside a frame
      return;
    }
    this.selfMoving = moveItem.itemid === this.item.itemid;

    if (
      !tacHelpers.isAncestor(tac, moveItem, this.item) &&
      !tacHelpers.isAncestor(tac, moveItem, this.item.connectsTo) &&
      (!this.item.connectsTo ||
        moveItem.itemid !== this.item.connectsTo.itemid) &&
      !this.selfMoving
    ) {
      // we're attached to something entirely different, or positioned at a lower position in the stack
      return;
    }

    let conns;
    let moveRight = 0;
    const move = tacHelpers.diffFromTac(tac, moveItem);

    if (this.localItem.onlyConnectTo) {
      // since we decided on a partner, drop the promiscuity and stick together
      const stack = tacHelpers.getTopAncestor(
        tac,
        this.localItem.onlyConnectTo
      );
      // find the tac pos of right stack
      const tacStack = tacHelpers.getItem(tac, stack.itemid);
      if (!tacStack) {
        // stack is missing since it was deleted.
        // 'this' will be destroyed so we dont _need_ cleanup, but drawing it makes it look faster
        this.localItem.connectsTo = null;
        this.draw(moveItem);
        return false;
      }
      conns = getMatchingConnections(this.outerPipe, {
        items: [tacStack],
      });

      moveRight = stack.itemid === moveItem.itemid ? move.x : 0;
    } else {
      conns = getMatchingConnections(this.outerPipe, tac);
    }

    const tacItem = tacHelpers.getItem(tac, this.item.itemid);
    const startPos = tacHelpers.getGlobalCoords(tacItem, tac);
    const globalPos = {
      x: startPos.x + move.x,
      y: startPos.y + move.y,
      z: startPos.z + move.z,
    };
    const targetY = this.item.height + globalPos.y;

    const usefulConnections = flatten(
      flatten(
        conns
          .map(conn => {
            const pos = tacHelpers.getGlobalCoords(conn, tac);
            return Object.assign({}, conn, pos);
          })
          .filter(conn => conn.x + moveRight > globalPos.x)
      ).map(parent =>
        parent.connections.map(connection => {
          const out = Object.assign({}, connection);

          out.x = connection.x + parent.x;
          out.y = connection.y + parent.y;
          out.z = connection.z + parent.z;
          out.parent = parent;
          return out;
        })
      )
    );

    const targetSlot = usefulConnections
      .filter(conn => conn.y === targetY)
      .sort((a, b) => (a.x > b.x ? 1 : b.x > a.x ? -1 : 0))[0];

    if (!targetSlot) {
      this.localItem.connectsTo = null;
      this.localItem.width = this.config.minWidth;
      this.draw(moveItem);
      return false;
    }
    const right = tacHelpers.getTopAncestor(tac, targetSlot.parent);

    // if we're on the wrong parent we drop the connection
    if (
      !this.selfMoving &&
      (move.x || move.y || move.z) &&
      tacHelpers.getTopAncestor(tac, moveItem).itemid !== moveItem.itemid
    ) {
      this.localItem.connectsTo = null;
      this.draw(moveItem);
      return false;
    }

    this.localItem.partnerBlocked = !this.railCanReach(
      tac,
      targetSlot,
      left,
      moveItem
    );

    if (this.localItem.partnerBlocked) {
      this.localItem.connectsTo = null;
      this.draw(moveItem);
      return false;
    }

    if (
      targetSlot &&
      this.localItem.onlyConnectTo &&
      targetSlot.parent.itemid !== this.localItem.onlyConnectTo.itemid
    ) {
      this.localItem.connectsTo = null;
      this.draw(moveItem);
      return false;
    }

    const preMoveItem = tacHelpers.getItem(tac, moveItem.itemid);
    const gPreMoveItem = tacHelpers.getGlobalCoords(preMoveItem, tac);

    let moveDistance = 0;
    if (moveItem.itemid === left.itemid) {
      moveDistance = gPreMoveItem.x - moveItem.x + move.x;
    }
    if (moveItem.itemid === right.itemid) {
      moveDistance = moveItem.x - gPreMoveItem.x + move.x;
    }

    const newWidth = targetSlot.x - globalPos.x + moveDistance;
    this.localItem.width = newWidth;
    this.localItem.connectsTo = targetSlot.parent;
    this.localItem.onlyConnectTo = targetSlot.parent;
    this.draw(moveItem);
    return true;
  }

  onSceneRedrawn(tac) {
    if (this.removing || !this.item) {
      this.destroy();
      return;
    }

    // update our copy
    this.item = tacHelpers.getItem(tac, this.item.itemid);

    if (!this.item) {
      this.destroy();
      return;
    }

    const { config, item, localItem } = this;

    const parent = tacHelpers.getParent(tac, item);
    const isExtendable = productService.isExtendable(this.item);

    if (
      isExtendable &&
      (!this.onItemMove(tac) || localItem.width > config.maxWidth)
    ) {
      this.removing = true;
      this.app.removeItem &&
        this.app.removeItem(item, {
          automatedUpdate: true,
        });
    } else if (
      isExtendable &&
      (item.width !== localItem.width ||
        (!item.connectsTo && localItem.connectsTo) ||
        (item.connectsTo &&
          localItem.connectsTo &&
          !deepEqual(item.connectsTo, localItem.connectsTo)))
    ) {
      const newItem = {
        ...item,
        width: localItem.width,
        connectsTo: localItem.connectsTo,
      };

      const hasChangedWidth = item.width !== localItem.width;

      this.app.updateItem &&
        this.app.updateItem(newItem, parent, {
          automatedUpdate: true,
          extended: hasChangedWidth,
        });
    } else if (!isExtendable && localItem.connectsTo) {
      const newItem = {
        ...this.item,
        connectsTo: null,
      };

      this.app.updateItem &&
        this.app.updateItem(newItem, parent, {
          automatedUpdate: true,
        });
    }
  }
}
